// The steam conveyor (category 'steamconveyor', the catalogue's 02.1 Steam room optionals
// group; docs/claude/products.md, *Steam conveyors*): a stainless-steel duct that
// carries a fan's air and a steam generator's steam into a steam room through its
// wall — the fan's Ø100 duct and the Ø28 copper steam pipe outside, a deflector or
// a grille in the room. Each plan is the horizontal section ("Sezione
// orizzontale") of Stenal's drawing 2020-11-09_Convogliatori_aria-vapore_
// Deflettore_e_griglia.dxf, the wall and the screws' thread hatching left out,
// turned half round (never mirrored) so its +y points into the room.

import type {
  ConnPort,
  GeomEntity,
  Geometry,
  ProductTemplate,
  Vec2,
} from './types';

/** A drawing's entities in the product's local frame (mm): lines
 *  [x1, y1, x2, y2], polylines, circles [x, y, r] and arcs [x, y, r, from°, to°]. */
interface Drawn {
  lines: number[][];
  polys: Array<{ closed: boolean; p: number[] }>;
  circles: number[][];
  arcs: number[][];
}

const DEFLECTOR_PLAN: Drawn = {
  lines: [
    [97.26, 80, -4.74, 80],
    [97.26, 78.5, -4.74, 78.5],
    [-21.69, 70.1, -21.09, 69.5],
    [-16.39, 69.5, -15.79, 70.1],
    [-21.69, 78.5, -21.69, 70.1],
    [-15.79, 70.1, -15.79, 78.5],
    [-21.69, 70.1, -15.79, 70.1],
    [-16.39, 69.5, -21.09, 69.5],
    [-14.49, 79.2, -22.99, 79.2],
    [-22.99, 80, -22.99, 79.2],
    [-14.49, 79.2, -14.49, 80],
    [-21.69, 79.2, -21.69, 78.5],
    [-15.79, 78.5, -15.79, 79.2],
    [-20.39, 80, -20.39, 69.5],
    [-17.09, 69.5, -17.09, 80],
    [-14.49, 80, -22.99, 80],
    [114.21, 70.1, 113.61, 69.5],
    [108.91, 69.5, 108.31, 70.1],
    [114.21, 78.5, 114.21, 70.1],
    [108.31, 70.1, 108.31, 78.5],
    [114.21, 70.1, 108.31, 70.1],
    [108.91, 69.5, 113.61, 69.5],
    [107.01, 79.2, 115.51, 79.2],
    [115.51, 80, 115.51, 79.2],
    [107.01, 79.2, 107.01, 80],
    [114.21, 79.2, 114.21, 78.5],
    [108.31, 78.5, 108.31, 79.2],
    [112.91, 80, 112.91, 69.5],
    [109.61, 69.5, 109.61, 80],
    [107.01, 80, 115.51, 80],
    [-20.74, 91.5, -16.74, 91.5],
    [-20.74, 91.5, -20.74, 73.95],
    [-16.74, 91.5, -16.74, 73.95],
    [-20.45, 73.7, -17.03, 73.7],
    [-16.74, 73.95, -17.03, 73.7],
    [-20.74, 73.95, -20.45, 73.7],
    [-18.74, 94.73, -18.74, 72.54],
    [-18.49, 93.69, -18.49, 92.99],
    [-18.99, 93.69, -18.99, 92.99],
    [-18.99, 92.99, -18.49, 92.99],
    [109.26, 91.5, 113.26, 91.5],
    [109.26, 91.5, 109.26, 73.95],
    [113.26, 91.5, 113.26, 73.95],
    [109.55, 73.7, 112.97, 73.7],
    [113.26, 73.95, 112.97, 73.7],
    [109.26, 73.95, 109.55, 73.7],
    [111.26, 94.73, 111.26, 72.54],
    [111.51, 93.69, 111.51, 92.99],
    [111.01, 93.69, 111.01, 92.99],
    [111.01, 92.99, 111.51, 92.99],
    [121.26, 140, 121.26, 90],
    [121.26, 90, 119.76, 90],
    [119.76, 110, 119.76, 132],
    [-28.74, 140, -28.74, 90],
    [-28.74, 90, -27.24, 90],
    [-27.24, 110, -27.24, 132],
    [94.26, 90, 94.26, 50],
    [94.26, 50, 92.76, 50],
    [92.76, 50, 92.76, 91.5],
    [-1.74, 90, -1.74, 50],
    [-1.74, 50, -0.24, 50],
    [-0.24, 50, -0.24, 91.5],
    [119.26, 90, 119.26, 98.34],
    [119.26, 101.59, 119.26, 108.47],
    [-26.74, 90, -26.74, 98.34],
    [-26.74, 101.59, -26.74, 108.47],
    [-1.74, 90, -26.74, 90],
    [-0.24, 91.5, -25.24, 91.5],
    [119.26, 90, 94.26, 90],
    [117.76, 91.5, 92.76, 91.5],
    [-25.24, 91.5, -25.24, 98.34],
    [-25.24, 101.59, -25.24, 108.47],
    [117.76, 91.5, 117.76, 98.34],
    [117.76, 101.59, 117.76, 108.47],
    [-26.74, 108.47, -25.24, 108.47],
    [117.76, 108.47, 119.26, 108.47],
    [-0.24, 50, 92.76, 50],
    [-0.24, 91.5, 92.76, 91.5],
    [-25.24, 108.47, 117.76, 108.47],
    [117.76, 98.34, 119.26, 98.34],
    [117.76, 101.59, 119.26, 101.59],
    [117.76, 101.59, 117.76, 98.34],
    [119.26, 101.59, 119.26, 98.34],
    [-26.74, 98.34, -25.24, 98.34],
    [-26.74, 101.59, -25.24, 101.59],
    [-26.74, 101.59, -26.74, 98.34],
    [-25.24, 101.59, -25.24, 98.34],
    [113.76, 91.5, 113.76, 90],
    [108.76, 91.5, 108.76, 90],
    [108.76, 91.5, 113.76, 91.5],
    [108.76, 90, 113.76, 90],
    [-21.24, 91.5, -21.24, 90],
    [-16.24, 91.5, -16.24, 90],
    [-16.24, 91.5, -21.24, 91.5],
    [-16.24, 90, -21.24, 90],
    [96.26, -140, 96.26, 80],
    [-3.74, -25, -3.74, 80],
    [-121.26, -140, 96.26, -140],
    [-121.26, -25, -3.74, -25],
    [-121.26, -25, -121.26, -140],
    [96.26, 80, -3.74, 80],
    [60.26, -4.44, 32.26, -4.44],
    [32.26, -74.75, 32.26, -4.44],
    [60.26, -74.75, 60.26, -4.44],
  ],
  polys: [
    { closed: true, p: [97.26, 78.5, 97.26, 80, 121.26, 80, 121.26, 78.5] },
    { closed: true, p: [-28.74, 78.5, -28.74, 80, -4.74, 80, -4.74, 78.5] },
    { closed: false, p: [-18.99, 93.69, -22.49, 92, -22.49, 91.5, -14.99, 91.5, -14.99, 92, -18.49, 93.69] },
    { closed: false, p: [111.01, 93.69, 107.51, 92, 107.51, 91.5, 115.01, 91.5, 115.01, 92, 111.51, 93.69] },
    { closed: false, p: [121.26, 138.5, 121.26, 140, -28.74, 140, -28.74, 138.5] },
    { closed: false, p: [-27.24, 132, 119.76, 132] },
    { closed: false, p: [119.76, 110, -27.24, 110] },
    { closed: false, p: [119.76, 90, -27.24, 90] },
  ],
  circles: [[-61.26, -82.5, 50.5], [-61.26, -82.5, 51.1], [46.26, -82.5, 14], [46.26, -82.5, 12.5], [46.26, -82.5, 16]],
  arcs: [],
};

const GRILLE_PLAN: Drawn = {
  lines: [
    [96.26, -117, 96.26, 103],
    [-3.74, -2, -3.74, 103],
    [-121.26, -117, 96.26, -117],
    [-121.26, -2, -3.74, -2],
    [-121.26, -2, -121.26, -117],
    [96.26, 103, -3.74, 103],
    [60.26, 18.56, 32.26, 18.56],
    [32.26, -51.75, 32.26, 18.56],
    [60.26, -51.75, 60.26, 18.56],
    [-1.74, 113, -1.74, 73],
    [-1.74, 73, -0.24, 73],
    [-0.24, 73, -0.24, 114.5],
    [94.26, 113, 94.26, 73],
    [94.26, 73, 92.76, 73],
    [92.76, 73, 92.76, 114.5],
    [-26.74, 113, -26.74, 114.5],
    [119.26, 113, 119.26, 114.5],
    [94.26, 113, 108.76, 113],
    [92.76, 114.5, 108.76, 114.5],
    [-16.24, 113, -1.74, 113],
    [-16.24, 114.5, -0.24, 114.5],
    [92.76, 73, -0.24, 73],
    [92.76, 114.5, -0.24, 114.5],
    [119.26, 114.5, 113.76, 114.5],
    [113.76, 114.5, 113.76, 113],
    [108.76, 114.5, 108.76, 113],
    [-21.24, 114.5, -21.24, 113],
    [-16.24, 114.5, -16.24, 113],
    [119.26, 113, 113.76, 113],
    [-21.24, 114.5, -26.74, 114.5],
    [-21.24, 113, -26.74, 113],
    [-16.24, 114.5, -21.24, 114.5],
    [-16.24, 113, -21.24, 113],
    [113.76, 114.5, 108.76, 114.5],
    [113.76, 113, 108.76, 113],
    [-4.74, 103, 97.26, 103],
    [-4.74, 101.5, 97.26, 101.5],
    [114.21, 93.1, 113.61, 92.5],
    [108.91, 92.5, 108.31, 93.1],
    [114.21, 101.5, 114.21, 93.1],
    [108.31, 93.1, 108.31, 101.5],
    [114.21, 93.1, 108.31, 93.1],
    [108.91, 92.5, 113.61, 92.5],
    [107.01, 102.2, 115.51, 102.2],
    [115.51, 103, 115.51, 102.2],
    [107.01, 102.2, 107.01, 103],
    [114.21, 102.2, 114.21, 101.5],
    [108.31, 101.5, 108.31, 102.2],
    [112.91, 103, 112.91, 92.5],
    [109.61, 92.5, 109.61, 103],
    [107.01, 103, 115.51, 103],
    [-21.69, 93.1, -21.09, 92.5],
    [-16.39, 92.5, -15.79, 93.1],
    [-21.69, 101.5, -21.69, 93.1],
    [-15.79, 93.1, -15.79, 101.5],
    [-21.69, 93.1, -15.79, 93.1],
    [-16.39, 92.5, -21.09, 92.5],
    [-14.49, 102.2, -22.99, 102.2],
    [-22.99, 103, -22.99, 102.2],
    [-14.49, 102.2, -14.49, 103],
    [-21.69, 102.2, -21.69, 101.5],
    [-15.79, 101.5, -15.79, 102.2],
    [-20.39, 103, -20.39, 92.5],
    [-17.09, 92.5, -17.09, 103],
    [-14.49, 103, -22.99, 103],
    [121.26, 114.5, 93.76, 114.5],
    [78.76, 114.5, 73.76, 114.5],
    [58.76, 114.5, 53.76, 114.5],
    [38.76, 114.5, 33.76, 114.5],
    [18.76, 114.5, 13.76, 114.5],
    [-1.24, 114.5, -28.74, 114.5],
    [121.26, 116.5, 93.76, 116.5],
    [78.76, 116.5, 73.76, 116.5],
    [58.76, 116.5, 53.76, 116.5],
    [38.76, 116.5, 33.76, 116.5],
    [18.76, 116.5, 13.76, 116.5],
    [-1.24, 116.5, -28.74, 116.5],
    [121.26, 116.5, 121.26, 114.5],
    [-28.74, 114.5, -28.74, 116.5],
    [93.76, 116.5, 93.76, 114.5],
    [78.76, 116.5, 78.76, 114.5],
    [73.76, 116.5, 73.76, 114.5],
    [58.76, 116.5, 58.76, 114.5],
    [53.76, 116.5, 53.76, 114.5],
    [38.76, 116.5, 38.76, 114.5],
    [33.76, 116.5, 33.76, 114.5],
    [18.76, 116.5, 18.76, 114.5],
    [13.76, 116.5, 13.76, 114.5],
    [-1.24, 116.5, -1.24, 114.5],
    [93.76, 114.5, 78.76, 114.5],
    [93.76, 116.5, 78.76, 116.5],
    [73.76, 114.5, 58.76, 114.5],
    [73.76, 116.5, 58.76, 116.5],
    [53.76, 114.5, 38.76, 114.5],
    [53.76, 116.5, 38.76, 116.5],
    [33.76, 114.5, 18.76, 114.5],
    [33.76, 116.5, 18.76, 116.5],
    [13.76, 114.5, -1.24, 114.5],
    [13.76, 116.5, -1.24, 116.5],
    [-17.49, 114.56, -17.49, 116.5],
    [-18.45, 114, -17.49, 114.56],
    [-19.99, 114.55, -19.04, 114],
    [-19.99, 116.5, -19.99, 114.55],
    [-18.45, 114, -19.04, 114],
    [-19.99, 116.5, -17.49, 116.5],
    [-18.12, 114.59, -18.12, 116.5],
    [-19.37, 114.59, -19.37, 116.5],
    [-20.74, 114.3, -22.74, 116.5],
    [-16.74, 114.3, -20.74, 114.3],
    [-16.74, 114.3, -16.74, 96.75],
    [-20.74, 114.3, -20.74, 96.75],
    [-17.03, 96.5, -20.45, 96.5],
    [-20.74, 96.75, -20.45, 96.5],
    [-16.74, 96.75, -17.03, 96.5],
    [-18.74, 117, -18.74, 95.34],
    [-14.74, 116.5, -16.74, 114.3],
    [-14.74, 116.5, -22.74, 116.5],
    [112.51, 114.56, 112.51, 116.5],
    [111.55, 114, 112.51, 114.56],
    [110.01, 114.55, 110.96, 114],
    [110.01, 116.5, 110.01, 114.55],
    [111.55, 114, 110.96, 114],
    [110.01, 116.5, 112.51, 116.5],
    [111.88, 114.59, 111.88, 116.5],
    [110.63, 114.59, 110.63, 116.5],
    [109.26, 114.3, 107.26, 116.5],
    [113.26, 114.3, 109.26, 114.3],
    [113.26, 114.3, 113.26, 96.75],
    [109.26, 114.3, 109.26, 96.75],
    [112.97, 96.5, 109.55, 96.5],
    [109.26, 96.75, 109.55, 96.5],
    [113.26, 96.75, 112.97, 96.5],
    [111.26, 117, 111.26, 95.34],
    [115.26, 116.5, 113.26, 114.3],
    [115.26, 116.5, 107.26, 116.5],
  ],
  polys: [
    { closed: true, p: [-4.74, 101.5, -4.74, 103, -28.74, 103, -28.74, 101.5] },
    { closed: true, p: [121.26, 101.5, 121.26, 103, 97.26, 103, 97.26, 101.5] },
  ],
  circles: [[-61.26, -59.5, 50.5], [-61.26, -59.5, 51.1], [46.26, -59.5, 14], [46.26, -59.5, 12.5], [46.26, -59.5, 16]],
  arcs: [[-17.8, 114.81, 0.38, 312.83, 325.02], [-17.8, 114.81, 0.38, 214.98, 287.17], [-18.74, 115.71, 1.28, 240.81, 299.19], [-19.68, 114.81, 0.38, 232.49, 325.02], [112.2, 114.81, 0.38, 312.83, 325.02], [112.2, 114.81, 0.38, 214.98, 287.17], [111.26, 115.71, 1.28, 240.81, 299.19], [110.32, 114.81, 0.38, 232.49, 325.02]],
};

// The drawing's own figures (mm): u along the wall from the duct's axis, v out of
// the room from the wall's inner face. The wall it is drawn in is 110 thick (10
// cladding, 80 EPS, 2 × 10).
const U_MIN = -75, U_MAX = 167.52, V_MAX = 230;
/** The duct through the wall (100 wide, from the EPS) and the box outside it. */
const BODY: ReadonlyArray<readonly [number, number]> = [[-50, 10], [50, 10], [50, 115], [167.52, 115], [167.52, 230], [-50, 230]];
/** The fan's collar (Ø101 inside) and the copper steam pipe (Ø28) on the box. */
const FAN = { u: 107.52, v: 172.5 };
const STEAM = { u: 0, v: 172.5, r: 14 };

/** A version of the conveyor: how far its room side (150 wide on the duct's
 *  axis) stands out of the wall, and its drawing. */
export interface ConveyorVersion { roomDepth: number; plan: Drawn }

/** A conveyor's footprint, its room side `roomDepth` deep, and a drawing point
 *  (u, v) in its local frame: the footprint's middle, turned half round. */
export function conveyorFrame(roomDepth: number): { width: number; depth: number; at: (u: number, v: number) => Vec2 } {
  const cu = (U_MIN + U_MAX) / 2, cv = (V_MAX - roomDepth) / 2;
  return { width: U_MAX - U_MIN, depth: V_MAX + roomDepth, at: (u, v) => ({ x: cu - u, y: cv - v }) };
}

/** Heights (mm above its base, the bottom of its room-side frame), from the
 *  drawing's vertical section: the frame 150 high round the duct's 100, the fan's
 *  collar 50 on the box, the copper pipe rising 100 from it. */
const Z = { frame: 150, duct: [25, 125], collar: 175, pipe: 225 } as const;
/** Its overall height. */
export const CONVEYOR_HEIGHT = Z.pipe;

/** Where a conveyor with its room side `roomDepth` deep turns when it is tilted:
 *  its duct's axis at the wall's inner face — in its local frame, from its
 *  footprint's middle, z above its base. */
export function conveyorPivot(roomDepth: number): { x: number; y: number; z: number } {
  const p = conveyorFrame(roomDepth).at(0, 0);
  return { x: p.x, y: p.y, z: (Z.duct[0] + Z.duct[1]) / 2 };
}

const circle = (c: Vec2, r: number, n = 32): Vec2[] => Array.from({ length: n }, (_, i) => ({ x: c.x + r * Math.cos((2 * Math.PI * i) / n), y: c.y + r * Math.sin((2 * Math.PI * i) / n) }));

/** The conveyor in 3D, its room side `roomDepth` deep: vertical prisms in its
 *  local frame — the room side, the duct with the box outside, the collar and
 *  the copper pipe. */
export function conveyorSolids(roomDepth: number): Array<{ ring: Vec2[]; z0: number; z1: number; copper?: boolean }> {
  const f = conveyorFrame(roomDepth);
  return [
    { ring: [f.at(-75, -roomDepth), f.at(75, -roomDepth), f.at(75, 0), f.at(-75, 0)], z0: 0, z1: Z.frame },
    { ring: BODY.map(([u, v]) => f.at(u, v)), z0: Z.duct[0], z1: Z.duct[1] },
    { ring: circle(f.at(FAN.u, FAN.v), 51.1), z0: Z.duct[1], z1: Z.collar },
    { ring: circle(f.at(STEAM.u, STEAM.v), STEAM.r), z0: Z.duct[1], z1: Z.pipe, copper: true },
  ];
}

// Fills carry their own alpha; the drawing's lines take the part's own colour.
const STEEL = '#b8bec466';
/** The steam pipe's copper, in its plan and its model (renderer/builtModels.ts). */
export const CONVEYOR_COPPER = '#c87533';

/** The conveyor's plan: its drawing over a steel fill of its body and its room
 *  side, the copper pipe in copper. Bounds are its footprint. */
export function conveyorGeometry(c: ConveyorVersion): Geometry {
  const f = conveyorFrame(c.roomDepth);
  const ring = (pts: ReadonlyArray<readonly [number, number]>) => pts.map(([u, v]) => f.at(u, v));
  const pt = (a: number[], i: number) => ({ x: a[i], y: a[i + 1] });
  const entities: GeomEntity[] = [
    { kind: 'hatch', loops: [ring(BODY)], solid: true, color: STEEL },
    { kind: 'hatch', loops: [ring([[-75, -c.roomDepth], [75, -c.roomDepth], [75, 0], [-75, 0]])], solid: true, color: STEEL },
    ...c.plan.lines.map((l): GeomEntity => ({ kind: 'line', a: pt(l, 0), b: pt(l, 2) })),
    ...c.plan.polys.map((p): GeomEntity => ({ kind: 'polyline', closed: p.closed, points: p.p.flatMap((_, i) => (i % 2 ? [] : [pt(p.p, i)])) })),
    ...c.plan.circles.map(([x, y, radius]): GeomEntity => ({ kind: 'circle', center: { x, y }, radius })),
    ...c.plan.arcs.map(([x, y, radius, startAngle, endAngle]): GeomEntity => ({ kind: 'arc', center: { x, y }, radius, startAngle, endAngle })),
    { kind: 'circle', center: f.at(STEAM.u, STEAM.v), radius: STEAM.r, color: CONVEYOR_COPPER },
  ];
  return { entities, bounds: { min: { x: -f.width / 2, y: -f.depth / 2 }, max: { x: f.width / 2, y: f.depth / 2 } } };
}

/** Where it connects, outside the wall: its fan (the collar) and its steam
 *  generator (the copper pipe). */
export function conveyorPorts(c: ConveyorVersion): ConnPort[] {
  const f = conveyorFrame(c.roomDepth);
  const n = (u: number, v: number) => { const p = f.at(u, v); return { nx: p.x / (f.width / 2), ny: p.y / (f.depth / 2) }; };
  return [
    { id: 'fan', name: 'Fan', ...n(FAN.u, FAN.v), type: 'plumbing' },
    { id: 'steam', name: 'Steam generator', ...n(STEAM.u, STEAM.v), type: 'plumbing' },
  ];
}

const DEFLECTOR: ConveyorVersion = { roomDepth: 50, plan: DEFLECTOR_PLAN };
const GRILLE: ConveyorVersion = { roomDepth: 4, plan: GRILLE_PLAN };

const COMMON: ProductTemplate['properties'] = [
  { key: 'manufacturer', label: 'Manufacturer', value: 'Stenal' },
  { key: 'fan', label: 'Fan connection', value: 'Ø101 int. collar, for a Ø100 duct' },
  { key: 'steam', label: 'Steam inlet', value: 'Ø28 copper pipe' },
  { key: 'duct', label: 'Through-wall duct', value: '100 × 100, 220 long', unit: 'mm' },
  { key: 'wall', label: 'Wall thickness (as drawn)', value: 110, unit: 'mm' },
  { key: 'material', label: 'Material', value: 'Brushed stainless steel 20/10 (in the room), stainless steel 15/10' },
];

const conveyor = (id: string, name: string, it: string, fr: string, c: ConveyorVersion, description: string, extra: ProductTemplate['properties']): ProductTemplate => {
  const f = conveyorFrame(c.roomDepth);
  return {
    id, name, nameI18n: { en: name, it, fr }, category: 'steamconveyor',
    width: f.width, depth: f.depth, height: CONVEYOR_HEIGHT, color: '#b8bec4',
    description, builtin: false, lockResize: true, throughWall: c.roomDepth,
    geometry: conveyorGeometry(c), ports: conveyorPorts(c), properties: [...COMMON, ...extra],
  };
};

/** The conveyors, with a deflector and with a grille: Baserow rows, written from
 *  this seed. */
export const STEAM_CONVEYOR_PRODUCTS: ProductTemplate[] = [
  conveyor('builtin-steam-conveyor-deflector', 'Steam Conveyor with Deflector', 'Convogliatore aria+vapore con deflettore', 'Convoyeur air+vapeur à déflecteur', DEFLECTOR,
    'Carries a fan’s air and a steam generator’s steam into a steam room through its wall; a deflector in the room turns the steam aside.', [
      { key: 'roomSide', label: 'Room side', value: 'Deflector 150 × 150, 50 deep', unit: 'mm' },
    ]),
  conveyor('builtin-steam-conveyor-grille', 'Steam Conveyor with Grille', 'Convogliatore aria+vapore con griglia', 'Convoyeur air+vapeur à grille', GRILLE,
    'Carries a fan’s air and a steam generator’s steam into a steam room through its wall, behind a grille flush with the wall.', [
      { key: 'roomSide', label: 'Room side', value: 'Grille 150 × 150 (cod. 20000760)', unit: 'mm' },
    ]),
];
